import React, { useState, useEffect } from 'react';
import { Plus, X, Check, ArrowUpCircle, ArrowDownCircle, History, Wallet as WalletIcon } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const Wallet = () => {
  const { user: me } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [myWallet, setMyWallet] = useState(null);
  const [myTransactions, setMyTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopup, setShowTopup] = useState(false);
  const [showReverse, setShowReverse] = useState(false);
  const [showTx, setShowTx] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState({ amount: '', description: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      if (me?.role === 'superadmin') {
        const res = await api.get('/api/wallet/all');
        setWallets(res.data.data || []);
      } else {
        const [walletRes, txRes] = await Promise.all([
          api.get('/api/wallet'),
          api.get('/api/wallet/transactions')
        ]);
        setMyWallet(walletRes.data.data);
        setMyTransactions(txRes.data.data || []);
      }
    } catch (err) { toast.error('Failed to load wallet'); }
    finally { setLoading(false); }
  };

  const handleTopup = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/wallet/topup', { user_id: selectedUser.user_id, ...form });
      toast.success(`₹${form.amount} credited to ${selectedUser.username}`);
      setShowTopup(false);
      setForm({ amount: '', description: '' });
      fetchData();
    } catch (err) { toast.error('Failed to topup'); }
  };

  const handleReverse = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/wallet/reverse', { user_id: selectedUser.user_id, ...form });
      toast.success(`₹${form.amount} reversed from ${selectedUser.username}`);
      setShowReverse(false);
      setForm({ amount: '', description: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to reverse'); }
  };

  const viewTransactions = async (w) => {
    setSelectedUser(w);
    try {
      const res = await api.get(`/api/wallet/user/${w.user_id}/transactions`);
      setTransactions(res.data.data || []);
    } catch (err) { setTransactions([]); }
    setShowTx(true);
  };

  const totalBalance = wallets.reduce((s, w) => s + parseFloat(w.balance || 0), 0);

  // DISTRIBUTOR / LCO VIEW
  if (me?.role !== 'superadmin') {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-title">My Wallet</div>
            <div className="page-subtitle">Your account balance and transactions</div>
          </div>
        </div>

        {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
          <>
            {/* Balance Card */}
            <div className="card" style={{ maxWidth: '360px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ background: 'rgba(0,212,170,0.15)', borderRadius: '12px', padding: '14px' }}>
                  <WalletIcon size={28} color="var(--accent-teal)" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Available Balance</div>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--accent-teal)' }}>
                    ₹{parseFloat(myWallet?.balance || 0).toFixed(2)}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{me?.role} Account</div>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontWeight: '600', fontSize: '15px' }}>
                Transaction History
              </div>
              {myTransactions.length === 0 ? (
                <div className="empty-state">No transactions yet</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Balance After</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myTransactions.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontSize: '12px' }}>{new Date(t.created_at).toLocaleString('en-IN')}</td>
                        <td>
                          <span className={`badge ${t.type === 'credit' ? 'badge-success' : 'badge-danger'}`}>
                            {t.type === 'credit' ? '▲' : '▼'} {t.type}
                          </span>
                        </td>
                        <td style={{ fontWeight: '600', color: t.type === 'credit' ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                          {t.type === 'credit' ? '+' : '-'}₹{parseFloat(t.amount).toFixed(2)}
                        </td>
                        <td>₹{parseFloat(t.balance_after).toFixed(2)}</td>
                        <td style={{ fontSize: '13px' }}>{t.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // SUPERADMIN VIEW
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Wallet Management</div>
          <div className="page-subtitle">Total balance: <strong style={{ color: 'var(--accent-teal)' }}>₹{totalBalance.toFixed(2)}</strong></div>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {wallets.map(w => (
                <tr key={w.id}>
                  <td>
                    <div style={{ fontWeight: '500' }}>{w.full_name || w.username}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{w.username}</div>
                  </td>
                  <td><span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{w.role}</span></td>
                  <td>
                    <span style={{ fontSize: '16px', fontWeight: '700', color: parseFloat(w.balance) > 0 ? 'var(--accent-teal)' : 'var(--text-secondary)' }}>
                      ₹{parseFloat(w.balance).toFixed(2)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => { setSelectedUser(w); setForm({ amount: '', description: '' }); setShowTopup(true); }}
                        className="btn btn-success" style={{ padding: '6px 12px', fontSize: '13px' }}>
                        <ArrowUpCircle size={14} /> Top Up
                      </button>
                      <button onClick={() => { setSelectedUser(w); setForm({ amount: '', description: '' }); setShowReverse(true); }}
                        className="btn btn-danger" style={{ padding: '6px 12px', fontSize: '13px' }}>
                        <ArrowDownCircle size={14} /> Reverse
                      </button>
                      <button onClick={() => viewTransactions(w)}
                        className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                        <History size={14} /> History
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Top Up Modal */}
      {showTopup && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowTopup(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Top Up — {selectedUser.full_name || selectedUser.username}</div>
              <button className="close-btn" onClick={() => setShowTopup(false)}><X size={20} /></button>
            </div>
            <div style={{ background: 'rgba(0,212,170,0.1)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Current Balance</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent-teal)' }}>₹{parseFloat(selectedUser.balance).toFixed(2)}</div>
            </div>
            <form onSubmit={handleTopup}>
              <div className="form-group">
                <label>Amount (₹) *</label>
                <input className="form-control" type="number" min="1" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required placeholder="Enter amount" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <input className="form-control" value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="e.g. Monthly recharge" />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowTopup(false)}>Cancel</button>
                <button type="submit" className="btn btn-success"><Check size={16} />Credit Wallet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reverse Modal */}
      {showReverse && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowReverse(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Reverse Payment — {selectedUser.full_name || selectedUser.username}</div>
              <button className="close-btn" onClick={() => setShowReverse(false)}><X size={20} /></button>
            </div>
            <div style={{ background: 'rgba(247,95,95,0.1)', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Current Balance</div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--accent-red)' }}>₹{parseFloat(selectedUser.balance).toFixed(2)}</div>
            </div>
            <form onSubmit={handleReverse}>
              <div className="form-group">
                <label>Amount to Reverse (₹) *</label>
                <input className="form-control" type="number" min="1" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} required placeholder="Enter amount" />
              </div>
              <div className="form-group">
                <label>Reason *</label>
                <input className="form-control" value={form.description} onChange={e => setForm({...form, description: e.target.value})} required placeholder="e.g. Incorrect payment" />
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowReverse(false)}>Cancel</button>
                <button type="submit" className="btn btn-danger"><Check size={16} />Reverse Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction History Modal */}
      {showTx && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowTx(false)}>
          <div className="modal" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Transactions — {selectedUser.full_name || selectedUser.username}</div>
              <button className="close-btn" onClick={() => setShowTx(false)}><X size={20} /></button>
            </div>
            {transactions.length === 0 ? (
              <div className="empty-state">No transactions found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Balance After</th>
                      <th>Description</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(t => (
                      <tr key={t.id}>
                        <td style={{ fontSize: '12px' }}>{new Date(t.created_at).toLocaleString('en-IN')}</td>
                        <td><span className={`badge ${t.type === 'credit' ? 'badge-success' : 'badge-danger'}`}>{t.type === 'credit' ? '▲' : '▼'} {t.type}</span></td>
                        <td style={{ fontWeight: '600', color: t.type === 'credit' ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                          {t.type === 'credit' ? '+' : '-'}₹{parseFloat(t.amount).toFixed(2)}
                        </td>
                        <td>₹{parseFloat(t.balance_after).toFixed(2)}</td>
                        <td style={{ fontSize: '13px' }}>{t.description || '—'}</td>
                        <td style={{ fontSize: '12px' }}>{t.done_by_name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
